import {
  REPLACED,
  NO_PERFECT_MATCH,
  NO_FOUND,
} from '../data/BomLineItemStatus';
import { NO_BOM, IMPORTING, QUOTED } from '../data/BomStatus';

import {
  isValidSelectedPart,
  hasValidSelectedPartSupplier,
  hasValidPartSupplierSelections,
  getValidTariffs,
  calculate_estimated_tariff,
} from './helpers';

export const DIGIKEY_CODE = 'digikey';
export const MOUSER_CODE = 'mouser-electronics';
export const ARROW_CODE = 'arrow';
export const VERICAL_CODE = 'verical';

const bomUnquotableStatus = ['unquotable', 'unprocessable'];

export const extractBom = (arg) => {
  if (!arg) return {};

  const { saved, selected_board_qty, totals } = arg;
  if (!totals || !selected_board_qty || !saved) return {};

  return {
    subTotal: totals[selected_board_qty],
    selected: true,
  };
};

export const findBomLineItemError = (
  status,
  stock,
  order_qty,
  part_number,
  quote_status,
  user_action,
) => {
  let error = null;
  if (quote_status === 'pending' || (user_action && user_action !== ''))
    return error;
  if (bomUnquotableStatus.includes(quote_status)) return "Couldn't quote part.";
  if (!part_number || part_number === '') error = 'Part without part number.';
  else if (status === NO_FOUND || status === NO_PERFECT_MATCH)
    error = 'Part not found.';
  else if (stock <= 0) error = 'Part out of stock.';
  else if (order_qty > stock)
    error = 'Not enough stock for part required quantity.';
  return error;
};

export const getPartSupplierSelections = (part, board_qty) => {
  if (!part || !hasValidPartSupplierSelections(part, board_qty)) return [];
  return part.ps_selections[board_qty];
};

export const getBomLineItemPricebreak = (part_supplier, board_qty) => {
  if (!part_supplier) return null;
  const { selected_pricebreak } = part_supplier;
  if (selected_pricebreak && selected_pricebreak[board_qty])
    return selected_pricebreak[board_qty];
  return null;
};

export const getQtyToBuy = (
  qty_to_buy,
  order_qty,
  board_qty,
  stock,
  user_action,
) => {
  if (
    !_.isEmpty(qty_to_buy) &&
    qty_to_buy[board_qty] &&
    qty_to_buy[board_qty] > 0
  )
    return qty_to_buy[board_qty] === order_qty ? qty_to_buy[board_qty] : order_qty;
  if (stock > 0 && stock >= order_qty) return order_qty;
  if (user_action === 'buy_stock') return stock;
  return 0;
};

export const getBomLineItemStatus = (status, selected_part) => {
  let selected_status = '';
  if (status && status !== '') selected_status = status;
  if (
    (selected_status === NO_PERFECT_MATCH || selected_status === NO_FOUND) &&
    !_.isEmpty(selected_part)
  )
    selected_status = REPLACED;
  return selected_status;
};

export const getDescription = (bom_line_item, board_qty, part_supplier) => {
  if (!bom_line_item) return '';
  if (bom_line_item.description && bom_line_item.description !== '')
    return bom_line_item.description;
  const { selected_part } = bom_line_item;
  const ps_selections = getPartSupplierSelections(selected_part, board_qty);
  if (ps_selections.length > 0) return ps_selections[0].description;
  return !_.isEmpty(part_supplier) ? part_supplier.description : '';
};

export const getPartSupplierMarkup = (selected_part_supplier) => {
  if (!selected_part_supplier) return 0;
  const { supplier } = selected_part_supplier;
  if (!supplier) return 0;
  switch (supplier.code) {
    case DIGIKEY_CODE:
      return 0;
    case MOUSER_CODE:
      return 0;
    case ARROW_CODE:
      return 0;
    case VERICAL_CODE:
      return 0;
    default:
      return 0;
  }
};

export const getBomCorrectStatus = (bom_status, remove_item, saved) => {
  if (remove_item || (!saved && bom_status === IMPORTING)) return NO_BOM;
  if (saved && bom_status === IMPORTING) return QUOTED;
  return bom_status;
};

export const getBomLineItemStock = (part, part_supplier, board_qty) => {
  if (!part) return 0;
  const ps_selections = getPartSupplierSelections(part, board_qty);
  if (ps_selections.length > 0)
    return ps_selections.reduce((acc, ps) => acc + ps.stock, 0);
  if (!part_supplier) return 0;
  return part_supplier.stock;
};

export const extractBomLineItemData = (bom_line_item, board_qty) => {
  if (!bom_line_item) return null;
  const { mpn, distributor_part_number, qty, status, qty_to_buy, user_action, selected_part } =
    bom_line_item;
  const board_status = status && status[board_qty] ? status[board_qty] : '';
  const new_status = getBomLineItemStatus(board_status, selected_part);
  const part_selected = isValidSelectedPart(new_status, selected_part)
    ? selected_part
    : null;
  const part_supplier_selected = hasValidSelectedPartSupplier(
    part_selected,
    board_qty,
  )
    ? part_selected.selected_part_supplier[board_qty]
    : null;

  const identifier = mpn || distributor_part_number;
  const description = getDescription(
    bom_line_item,
    board_qty,
    part_supplier_selected,
  );
  const stock = getBomLineItemStock(
    part_selected,
    part_supplier_selected,
    board_qty,
  );
  const order_qty =
    part_supplier_selected ||
      hasValidPartSupplierSelections(part_selected, board_qty)
      ? qty * board_qty + part_selected.extra_parts[board_qty]
      : 0;
  const markup = !hasValidPartSupplierSelections(part_selected, board_qty)
    ? getPartSupplierMarkup(part_supplier_selected)
    : 0;

  const tariffs = getBomLineItemTariffs(part_selected, part_supplier_selected, board_qty)
  const {estimated_tariff, tariff_may_be_applied_after_purchase} = getBomLineItemEstimatedTariff(part_selected, part_supplier_selected, board_qty, qty_to_buy)

  return {
    identifier,
    description,
    stock,
    order_qty,
    markup,
    new_status,
    part_selected,
    part_supplier_selected,
    tariffs,
    estimated_tariff,
    tariff_may_be_applied_after_purchase,
  };
};

export const getBomLineItemEstimatedTariff = (part, part_supplier, board_qty, qty_to_buy) => {
  if(!part) return {estimated_tariff: 0, tariff_may_be_applied_after_purchase: false}
  let tariff_may_be_applied_after_purchase = false
  const ps_selections = getPartSupplierSelections(part, board_qty)
  if(ps_selections.length > 0) {
    const estimated_tariff = ps_selections.map((ps) => {
      const s_code = ps.supplier.code
      const part_tariffs = getValidTariffs(ps.taxes, s_code)
      const extended_price = ps.qty_to_buy * (ps.selected_pricebreak && ps.selected_pricebreak[board_qty] ? ps.selected_pricebreak[board_qty].price : 0)
      return calculate_estimated_tariff(part_tariffs, extended_price)
    }).reduce((acc, ps_estimated_tariff) => acc + ps_estimated_tariff, 0)
    return {
      estimated_tariff,
      tariff_may_be_applied_after_purchase,
    }
  }
  if(!part_supplier) return {estimated_tariff: 0, tariff_may_be_applied_after_purchase: false}
  const ps = part_supplier
  const s_code = ps.supplier.code
  const part_tariffs = getValidTariffs(ps.taxes, s_code)
  tariff_may_be_applied_after_purchase = ps.taxes && ps.taxes.length > 0 && part_tariffs.length == 0
  const correct_qty_to_buy = ps.qty_to_buy || (qty_to_buy && qty_to_buy[board_qty] ? qty_to_buy[board_qty] : 0)
  const extended_price = correct_qty_to_buy * (ps.selected_pricebreak && ps.selected_pricebreak[board_qty] ? ps.selected_pricebreak[board_qty].price : 0)
  return {
    estimated_tariff: calculate_estimated_tariff(part_tariffs, extended_price),
    tariff_may_be_applied_after_purchase: s_code === 'digikey' && ps.taxes && ps.taxes.length > 0 && part_tariffs.length == 0,
  }
}

export const getBomLineItemTariffs = (part, part_supplier, board_qty) => {
  if(!part) return []
  const ps_selections = getPartSupplierSelections(part, board_qty)
  if(ps_selections.length > 0) return ps_selections.map((ps) => getValidTariffs(ps.taxes, ps.supplier.code)).flat()
  if(!part_supplier) return []
  return getValidTariffs(part_supplier.taxes, part_supplier.supplier.code)
}

export const getPartSupplierPrice = (part_supplier, board_qty) => {
  if (!part_supplier) return null;
  const { selected_pricebreak } = part_supplier;
  if (selected_pricebreak && selected_pricebreak[board_qty]) {
    const markup = getPartSupplierMarkup(part_supplier);
    return selected_pricebreak[board_qty].price * (1 + markup);
  }
  return null;
};

export const getBomLineItemExtendedPrice = (
  part,
  part_supplier,
  board_qty,
  qty_to_buy,
) => {
  if (!part) return null;
  const ps_selections = getPartSupplierSelections(part, board_qty);
  if (ps_selections.length > 0)
    return ps_selections.reduce(
      (acc, ps) =>
        acc + (getPartSupplierPrice(ps, board_qty) || 0) * ps.qty_to_buy,
      0,
    );
  if (!part_supplier) return null;
  const ext_price = getPartSupplierPrice(part_supplier, board_qty);
  return ext_price ? ext_price * qty_to_buy : null;
};

export const getBomLineItemExtraData = (
  bom_line_item,
  board_qty,
) => {
  if (!bom_line_item) return {};
  const { qty_to_buy, quote_status, user_action } = bom_line_item;

  const {
    new_status,
    identifier,
    part_selected,
    part_supplier_selected,
    stock,
    order_qty,
    markup,
  } = extractBomLineItemData(bom_line_item, board_qty);

  const new_qty_to_buy = getQtyToBuy(
    qty_to_buy,
    order_qty,
    board_qty,
    stock,
    user_action,
  );
  const new_price_by_qty = getBomLineItemExtendedPrice(
    part_selected,
    part_supplier_selected,
    board_qty,
    new_qty_to_buy,
  );
  const ps_selections_markups = getPartSupplierSelections(
    part_selected,
    board_qty,
  ).map((ps) => getPartSupplierMarkup(ps));
  const error_message = findBomLineItemError(
    new_status,
    stock,
    order_qty,
    identifier,
    quote_status,
    user_action,
  );

  return {
    error_message,
    markup,
    new_status,
    new_qty_to_buy,
    new_price_by_qty,
    ps_selections_markups,
  };
};